/**
 * What the page template reads from the view model, the same way for every class: rows by
 * their path or role, the validity status, and how validation results map to rows.
 */

import type { EntityViewModel } from '@hooks/useEntityView';
import { resultLevel, shapeName, type ResultLevel, type ValidationResult } from '@hooks/useValidationResults';
import { shortenUri } from '@hooks/useEntity';
import type { PrefixMap } from '@utils/prefix.utils';
import { DASH, leadingPredicate, pathToSparql, type ViewPropertyShape } from '@utils/viewShapes.utils';
import type { EntityObject, EntityProperty } from '@/types';

export const BNO = 'https://data.banenor.no/ontology/';
const HAS_BEGINNING_STAMP = `${BNO}hasBeginningTimeStamp`;
const HAS_END_STAMP = `${BNO}hasEndTimeStamp`;

export function valuesOf(model: EntityViewModel, property: ViewPropertyShape | undefined): EntityObject[] {
  return property ? (model.values[property.id] ?? []) : [];
}

export function keyedValues(model: EntityViewModel, key: string): EntityObject[] {
  return valuesOf(model, model.rows[key]);
}

/** How many resources a keyed row has, whether it is a plain row or a table. */
export function rowCount(model: EntityViewModel, key: string): number {
  const row = model.rows[key];
  return row ? (model.values[row.id]?.length ?? 0) + (model.tables[row.id]?.items.length ?? 0) : 0;
}

const stampOf = (properties: EntityProperty[], predicate: string): string | null =>
  properties.find((property) => property.predicate === predicate)?.objects[0]?.value ?? null;

export type ValidityStatus = 'inUse' | 'expired' | 'planned' | 'unknown';

export interface Validity {
  status: ValidityStatus;
  from: string | null;
  to: string | null;
}

export function validityOf(properties: EntityProperty[], now = new Date()): Validity {
  const from = stampOf(properties, HAS_BEGINNING_STAMP);
  const to = stampOf(properties, HAS_END_STAMP);
  if (!from && !to) return { status: 'unknown', from, to };
  if (to && new Date(to) <= now) return { status: 'expired', from, to };
  if (from && new Date(from) > now) return { status: 'planned', from, to };
  return { status: 'inUse', from, to };
}

/** Results by the property shape they come from: matched on the shape's local name, since
 * reports from before the shape refactor name shapes in the data namespace. */
export function resultsByRow(
  model: EntityViewModel,
  results: ValidationResult[]
): Map<string, ValidationResult[]> {
  const byName = new Map(model.allProperties.map((property) => [shapeName(property.id), property.id]));
  const byPath = new Map(
    model.allProperties
      .map((property) => [leadingPredicate(property.path), property.id] as const)
      .filter(([predicate]) => predicate)
  );
  const map = new Map<string, ValidationResult[]>();
  for (const result of results) {
    if (result.focusNode !== model.uri) continue;
    const id = byName.get(shapeName(result.sourceShape)) ?? (result.resultPath ? byPath.get(result.resultPath) : undefined);
    if (!id) continue;
    map.set(id, [...(map.get(id) ?? []), result]);
  }
  return map;
}

export function worstLevel(results: ValidationResult[] | undefined): ResultLevel | null {
  if (!results || results.length === 0) return null;
  const levels = results.map(resultLevel);
  return levels.includes('error') ? 'error' : levels.includes('warn') ? 'warn' : 'info';
}

export type CheckState = 'pass' | 'warn' | 'gap' | 'notChecked';

/** How a fact row stands against validation: checked rows pass or warn, required rows without
 * a value are gaps, and view-only projections are not checked. */
export function checkState(
  property: ViewPropertyShape,
  values: EntityObject[],
  results: ValidationResult[] | undefined
): CheckState {
  if (results && results.length > 0) return 'warn';
  if (values.length === 0 && (property.minCount ?? 0) > 0) return 'gap';
  if (property.deactivated) return 'notChecked';
  return 'pass';
}

export const isTitleRow = (property: ViewPropertyShape) =>
  property.role === DASH.LabelRole || property.role === DASH.DescriptionRole;
export const isKeyInfoRow = (property: ViewPropertyShape) => property.role === DASH.KeyInfoRole;

/** Every IRI on the page worth a label: row values, nested values and table items. */
export function pageIris(model: EntityViewModel, limit = 2500): string[] {
  // The entity itself first: its name is the page's title and the hero's label
  const iris = new Set<string>(model.uri ? [model.uri] : []);
  const add = (values: EntityObject[] | undefined) => {
    for (const value of values ?? []) if (value.type === 'uri' && iris.size < limit) iris.add(value.value);
  };
  Object.values(model.values).forEach(add);
  Object.values(model.nestedValues).forEach((byProperty) => Object.values(byProperty).forEach(add));
  for (const table of Object.values(model.tables)) {
    for (const item of table.items) {
      if (iris.size >= limit) break;
      if (!item.cells.item) iris.add(item.iri);
      Object.values(item.cells).forEach(add);
    }
  }
  return Array.from(iris);
}

/** The nodes whose validation results the page shows: the record itself, not the objects on it. */
export function focusIris(model: EntityViewModel): string[] {
  return [model.uri];
}

/** A SHACL path as SPARQL with prefixed names: ^bno:onRailwayTrack. */
export function prettyPath(property: ViewPropertyShape, prefixes: PrefixMap): string {
  return pathToSparql(property.path)
    .replace(/<([^>]+)>/g, (_, iri: string) => shortenUri(iri, prefixes))
    .replace(/\^\(([^()|/]+)\)/g, '^$1')
    .replace(/\(([^()|/^]+)\)/g, '$1');
}

export const ERA = 'http://data.europa.eu/949/';

